import { AccountState } from '@seapoint/server/enums';
import { ColumnDef } from '@tanstack/react-table';
import { CircleAlert } from 'lucide-react';
import { AvatarNameDescription } from '../../../design-system/avatar/avatar-name-description/avatar-name-description';
import { CopyIcon } from '../../../design-system/copy-icon/copy-icon';
import { ColumnHeaderWithSorting } from '../../../design-system/data-table/column-header-with-sorting';
import { Inline } from '../../../design-system/inline/inline';
import { Tooltip } from '../../../design-system/tooltip/tooltip';
import { AccountView } from '../../../types/entity-types';
import { getAccountDisplayName } from '../../../utils/accounts/get-account-display-name';
import { isBankingAccount } from '../../../utils/accounts/is-banking-account';
import { getAmountWithCurrency } from '../../../utils/get-amount-with-currency';
import { getFormattedIban } from '../../../utils/get-formatted-iban';
import { getFormattedSortCode } from '../../../utils/get-formatted-sort-code';
import { getInstitutionDetails } from '../utils/get-institution-details';
import { AccountOptionsButton } from './account-options-button';

export const accountTableColumnsDefinition: ColumnDef<AccountView>[] = [
  {
    id: 'account',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Account" />,
    cell: ({ row }) => {
      const account = row.original;
      const institution = account.institution;
      const institutionDetails = institution ? getInstitutionDetails(institution) : undefined;

      const accountName = getAccountDisplayName(account);
      const logoUrl = institutionDetails?.logo;
      const isUnlinked = isBankingAccount(account) && account.state === AccountState.UNLINKED;

      return (
        <Inline space={2} align="center" wrap="nowrap">
          <AvatarNameDescription
            avatar={{ showAvatar: true, avatarUrl: logoUrl, size: 'sm' }}
            name={accountName ?? ''}
            // Arbitrary width to make sure the column doesn't take too much space
            // And to avoid there are no jumps in UI when navigating through pages
            // TODO: Tanstack table has a size property, but it doesn't work as expected. Need to investigate further
            // https://github.com/TanStack/table/issues/5115
            className="w-56"
          />
          {isUnlinked && (
            <Tooltip
              trigger={<CircleAlert className="h-4 w-4 shrink-0 text-chart-5" />}
              content="Connection error. Please reauthorise account."
              contentAlign="center"
            />
          )}
        </Inline>
      );
    },
    accessorFn: (row) => getAccountDisplayName(row),
    size: 28,
  },
  {
    id: 'accountNumber',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Account Number / IBAN" />,
    cell: ({ row }) => {
      if (!isBankingAccount(row.original)) {
        return <p className="text-sm">-</p>;
      }

      const { iban, accountNo } = row.original.accountInformation || {};
      const displayValue = accountNo || getFormattedIban(iban);
      const copyValue = accountNo || iban;
      return (
        <Inline className="w-full min-w-0 pr-2" space={2} wrap="nowrap" align="center">
          <p className="min-w-0 flex-1 truncate text-sm" title={copyValue ?? ''}>
            {displayValue}
          </p>
          {copyValue && (
            <div className="flex-none">
              <CopyIcon textToCopy={copyValue} iconSize="sm" tooltipConfig={{ visible: true, label: 'Copy' }} />
            </div>
          )}
        </Inline>
      );
    },
    accessorFn: (row) =>
      isBankingAccount(row) ? row.accountInformation?.accountNo || row.accountInformation?.iban : null,
    size: 35,
  },
  {
    id: 'sortCode',
    accessorKey: 'accountInformation.sortCode',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Sort Code" />,
    cell: ({ row }) => {
      if (!isBankingAccount(row.original)) {
        return <p className="text-sm">-</p>;
      }

      const sortCode = row.original.accountInformation?.sortCode;
      const formattedSortCode = getFormattedSortCode(sortCode);

      return (
        <Inline className="w-full min-w-0 pr-1" space={2} wrap="nowrap" align="center">
          <p className="min-w-0 flex-1 text-sm">{formattedSortCode}</p>
          {sortCode && (
            <div className="flex-none">
              <CopyIcon textToCopy={sortCode} iconSize="sm" tooltipConfig={{ visible: true, label: 'Copy' }} />
            </div>
          )}
        </Inline>
      );
    },
    size: 17,
    minSize: 17,
  },
  {
    id: 'balance',
    accessorKey: 'totalBalance',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Account Balance" />,
    cell: ({ row }) => {
      const { totalBalance: balance, currency } = row.original;

      const balanceWithCurrency = getAmountWithCurrency({ amount: balance ?? 0, currency, isAccountBalance: true });

      return <p className="text-right text-sm">{balanceWithCurrency}</p>;
    },
    size: 10,
  },
  {
    id: 'options',
    accessorKey: 'options',
    // No header for the ellipsis column
    header: () => null,
    cell: ({ row, table }) => {
      if (!table.options.meta?.showAccountOptions) {
        // Show an empty div to align the column with the other columns
        return <div className="w-8" />;
      }
      const onDisconnectAccount = table.options.meta?.onDisconnectAccount;
      const onReconnectAccount = table.options.meta?.onReconnectAccount;
      return (
        <AccountOptionsButton
          onDisconnectAccount={async () => {
            await onDisconnectAccount?.(row.original.id);
          }}
          onReconnectAccount={async () => {
            await onReconnectAccount?.(row.original);
          }}
        />
      );
    },
    size: 5,
    minSize: 5,
  },
];
